import { notifications } from '@mantine/notifications'
import { TbCheck, TbX, TbInfoCircle, TbAlertTriangle } from 'react-icons/tb'

export type NotificationType = 'success' | 'error' | 'info' | 'warning'

export interface NotificationOptions {
  title?: string
  message: string
  type?: NotificationType
  autoClose?: boolean | number
  position?: 'top-left' | 'top-right' | 'top-center' | 'bottom-left' | 'bottom-right' | 'bottom-center'
}

// Default icons for different notification types
const notificationIcons = {
  success: <TbCheck size={18} />,
  error: <TbX size={18} />,
  info: <TbInfoCircle size={18} />,
  warning: <TbAlertTriangle size={18} />
}

// Default colors for different notification types
const notificationColors = {
  success: 'green',
  error: 'red', 
  info: 'blue',
  warning: 'yellow'
}

/**
 * Simple notification wrapper with predefined types and styling
 * Note: All text should be provided by the consuming application for i18n
 */
export const notify = {
  /**
   * Show a success notification
   * @param message - The notification message (should be translated by consuming app)
   * @param options - Additional options including title (should be translated by consuming app)
   */
  success: (message: string, options?: Omit<NotificationOptions, 'type' | 'message'>) => {
    notifications.show({
      title: options?.title,
      message,
      color: 'green',
      icon: notificationIcons.success,
      autoClose: options?.autoClose !== false ? 4000 : false,
    })
  },

  /**
   * Show an error notification
   * @param message - The notification message (should be translated by consuming app)
   * @param options - Additional options including title (should be translated by consuming app)
   */
  error: (message: string, options?: Omit<NotificationOptions, 'type' | 'message'>) => {
    notifications.show({
      title: options?.title,
      message,
      color: 'red',
      icon: notificationIcons.error,
      autoClose: options?.autoClose !== false ? 6000 : false,
    })
  },

  /**
   * Show an info notification
   * @param message - The notification message (should be translated by consuming app)
   * @param options - Additional options including title (should be translated by consuming app)
   */
  info: (message: string, options?: Omit<NotificationOptions, 'type' | 'message'>) => {
    notifications.show({
      title: options?.title,
      message,
      color: 'blue',
      icon: notificationIcons.info,
      autoClose: options?.autoClose !== false ? 4000 : false,
    })
  },

  /**
   * Show a warning notification
   * @param message - The notification message (should be translated by consuming app)
   * @param options - Additional options including title (should be translated by consuming app)
   */
  warning: (message: string, options?: Omit<NotificationOptions, 'type' | 'message'>) => {
    notifications.show({
      title: options?.title,
      message,
      color: 'yellow',
      icon: notificationIcons.warning,
      autoClose: options?.autoClose !== false ? 5000 : false,
    })
  },

  /**
   * Show a custom notification
   * @param options - Notification options including message and title (should be translated by consuming app)
   */
  show: (options: NotificationOptions) => {
    const type = options.type || 'info'
    notifications.show({
      title: options.title,
      message: options.message,
      color: notificationColors[type],
      icon: notificationIcons[type],
      autoClose: options.autoClose !== false ? 4000 : false,
      position: options.position || 'top-right',
    })
  },

  /**
   * Hide all notifications
   */
  clean: () => {
    notifications.clean()
  },

  /**
   * Hide specific notification by ID
   */
  hide: (id: string) => {
    notifications.hide(id)
  }
}

// Export the original notifications API as well for advanced usage
export { notifications }
